import { useEffect, useMemo, useState } from "react";
import api from "../api/axiosInstance";

const monographStatusCache = new Map();
const monographStatusRequests = new Map();

const getCachedStatuses = (identifiers) => {
  const cachedStatuses = {};

  identifiers.forEach((id) => {
    const cachedStatus = monographStatusCache.get(id);
    if (cachedStatus) {
      cachedStatuses[id] = cachedStatus;
    }
  });

  return cachedStatuses;
};

export function useMonographStatuses(identifiers) {
  const normalizedIdentifiers = useMemo(() => {
    if (!Array.isArray(identifiers)) return [];
    return identifiers
      .map((id) => (id == null ? "" : String(id).trim()))
      .filter(Boolean);
  }, [identifiers]);

  const uniqueIdentifiers = useMemo(
    () => Array.from(new Set(normalizedIdentifiers)),
    [normalizedIdentifiers]
  );
  const identifiersKey = uniqueIdentifiers.join("|");

  const [data, setData] = useState(() => getCachedStatuses(uniqueIdentifiers));
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const effectIdentifiers = identifiersKey ? identifiersKey.split("|") : [];

    if (effectIdentifiers.length === 0) {
      setData({});
      setLoading(false);
      setErr(null);
      return;
    }

    let cancelled = false;
    const cachedStatuses = getCachedStatuses(effectIdentifiers);
    setData(cachedStatuses);

    const missingIdentifiers = effectIdentifiers.filter(
      (id) => !monographStatusCache.has(id)
    );

    if (missingIdentifiers.length === 0) {
      setLoading(false);
      setErr(null);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    setErr(null);

    const requestKey = missingIdentifiers.join("|");
    let request = monographStatusRequests.get(requestKey);

    if (!request) {
      const params = new URLSearchParams();
      missingIdentifiers.forEach((id) => params.append("identifiers", id));

      request = api
        .get(`/drugs/monograph?${params.toString()}`)
        .then((res) => {
          const fetchedStatuses = {};
          (res.data || []).forEach((item) => {
            if (item?.identifier != null) {
              fetchedStatuses[String(item.identifier)] = item;
            }
          });

          missingIdentifiers.forEach((id) => {
            monographStatusCache.set(id, fetchedStatuses[id] || null);
          });
        })
        .finally(() => {
          monographStatusRequests.delete(requestKey);
        });

      monographStatusRequests.set(requestKey, request);
    }

    request
      .then(() => {
        if (cancelled) return;
        setData(getCachedStatuses(effectIdentifiers));
      })
      .catch((e) => {
        if (!cancelled) setErr(e);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [identifiersKey]);

  return { data, loading, error: err };
}
